import { Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import type { OidcProvider } from "../../lib/auth";

/**
 * Official provider brand marks. Brand logos are a documented exception to the
 * "no hardcoded colours" design-system rule — they must render in their
 * trademarked colours to be recognisable and are not themeable tokens.
 */
function GoogleGlyph() {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden focusable={false}>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.582c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.166 6.656 3.582 9 3.582Z"
      />
    </svg>
  );
}

function MicrosoftGlyph() {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden focusable={false}>
      <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
      <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
      <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
      <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
    </svg>
  );
}

interface SsoProviderButtonProps {
  provider: OidcProvider;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Accessible "Continue with <provider>" button for the admin login page.
 * Button chrome uses semantic theme tokens; only the brand glyph carries brand
 * colours. Shown generically (both providers) to avoid IdP enumeration.
 */
export function SsoProviderButton({
  provider,
  label,
  onClick,
  disabled = false,
  loading = false,
}: SsoProviderButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-busy={loading}
      className={cn(
        "flex w-full items-center justify-center gap-3 rounded-lg border border-input bg-background px-4 py-2.5",
        "text-sm font-medium text-foreground transition-colors",
        "hover:bg-muted",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        "disabled:cursor-not-allowed disabled:opacity-50",
      )}
    >
      {loading ? (
        <Loader2 className="h-[18px] w-[18px] animate-spin" aria-hidden />
      ) : provider === "google" ? (
        <GoogleGlyph />
      ) : (
        <MicrosoftGlyph />
      )}
      <span>{label}</span>
    </button>
  );
}
